import { useEffect, useRef, useState } from 'react'
import mapboxgl from 'mapbox-gl'

interface UseMapboxMapOptions {
  styleUrl?: string
  /** If false, scroll/wheel over the map will not zoom the map (page scroll instead). Default true. */
  scrollZoom?: boolean
}

export interface UseMapboxMapReturn {
  map: React.MutableRefObject<mapboxgl.Map | null>
  mapContainer: React.MutableRefObject<HTMLDivElement | null>
  isLoaded: boolean
  styleLoaded: boolean
}

/**
 * Hook for initializing and managing Mapbox map instance
 * Handles map creation, style loading, and cleanup
 */
export function useMapboxMap(options?: UseMapboxMapOptions): UseMapboxMapReturn {
  const mapContainer = useRef<HTMLDivElement>(null)
  const map = useRef<mapboxgl.Map | null>(null)
  const [isLoaded, setIsLoaded] = useState(false)
  const [styleLoaded, setStyleLoaded] = useState(false)

  const styleUrl =
    options?.styleUrl || 'mapbox://styles/ayott/cmdldmyxv00eu01ptcd7acb7k'
  const scrollZoom = options?.scrollZoom ?? true

  // Track the current style URL to avoid unnecessary updates
  const currentStyleUrlRef = useRef<string | null>(null)

  // Initialize map only once
  useEffect(() => {
    if (!mapContainer.current || map.current) return

    mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_PUBLIC_TOKEN

    // Default center - will be updated when user location is available
    const defaultCenter: [number, number] = [-98.5795, 39.8283] // Approximate center of US
    const defaultZoom = 4 // Zoomed out to show more of the country

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: styleUrl,
      center: defaultCenter,
      zoom: defaultZoom,
      scrollZoom
    })

    // Suppress known harmless Mapbox warnings that clutter the console
    // These are internal Mapbox GL JS warnings and don't affect functionality:
    // - Style feature selector warnings (style configuration issues)
    // - Style diff rebuild warnings (expected when switching between styles)
    // - Terrain source warnings (styles may reference terrain that's not always available)
    // - WebGL driver warnings (graphics driver compatibility issues)
    const originalWarn = console.warn
    console.warn = (...args: any[]) => {
      const message = args[0]?.toString() || ''
      const stack = args[1]?.stack?.toString() || ''
      
      // Only suppress warnings that are clearly from Mapbox GL JS
      const isMapboxWarning = 
        message.includes('mapbox-gl.js') ||
        stack.includes('mapbox-gl.js') ||
        message.includes('featureNamespace') ||
        message.includes('featureset') ||
        message.includes('Unable to perform style diff') ||
        message.includes("Couldn't find terrain source") ||
        message.includes('mapbox-dem')
      
      // Suppress WebGL warnings (usually graphics driver issues, harmless)
      const isWebGLWarning = 
        message.includes('GL_INVALID_VALUE') ||
        message.includes('glBindAttribLocation')
      
      if (isMapboxWarning || isWebGLWarning) {
        return // Suppress these warnings
      }
      
      // Allow other warnings through
      originalWarn.apply(console, args)
    }

    // Track initial style URL
    currentStyleUrlRef.current = styleUrl

    // Add navigation controls with pitch enabled for 3D terrain viewing
    map.current.addControl(
      new mapboxgl.NavigationControl({
        showCompass: true,
        showZoom: true,
        visualizePitch: true
      }),
      'top-right'
    )

    map.current.on('load', () => {
      setIsLoaded(true)
    })

    // Wait for style to be fully loaded and map to be idle before marking as ready
    // This ensures all layers and sources are properly initialized
    const checkStyleReady = () => {
      if (map.current?.isStyleLoaded() && map.current.loaded()) {
        setStyleLoaded(true)
        return true
      }
      return false
    }

    // Listen for style load events
    map.current.on('styledata', () => {
      // Wait for map to be idle after style loads to ensure everything is ready
      if (map.current) {
        map.current.once('idle', () => {
          if (checkStyleReady()) {
            // Style is fully ready
          }
        })
      }
    })

    // Also check on load event
    map.current.on('load', () => {
      map.current?.once('idle', () => {
        checkStyleReady()
      })
    })

    // Fallback: check style loaded status periodically for the first few seconds
    let styleReady = false
    const styleCheckInterval = setInterval(() => {
      if (checkStyleReady()) {
        styleReady = true
        clearInterval(styleCheckInterval)
      }
    }, 200)

    // Clear interval after 10 seconds
    setTimeout(() => {
      clearInterval(styleCheckInterval)
      // Final check - if still not ready, mark as ready anyway to prevent infinite waiting
      if (!styleReady && map.current) {
        checkStyleReady()
      }
    }, 10000)

    // Cleanup function - only run when component unmounts
    return () => {
      // Restore original console.warn
      console.warn = originalWarn
      
      // Only remove map when component actually unmounts
      if (map.current) {
        map.current.remove()
        map.current = null
      }
    }
  }, [scrollZoom]) // scrollZoom is intentional: map is created once per value

  // Resize map when container dimensions change (e.g. sidebar or right panel close).
  // Debounce so we resize once after layout settles and avoid flash from resizing mid-transition.
  useEffect(() => {
    const container = mapContainer.current
    const mapInstance = map.current
    if (!container || !mapInstance) return

    let timeoutId: ReturnType<typeof setTimeout> | null = null
    const resizeObserver = new ResizeObserver(() => {
      if (timeoutId) clearTimeout(timeoutId)
      timeoutId = setTimeout(() => {
        timeoutId = null
        if (map.current) map.current.resize()
      }, 150)
    })
    resizeObserver.observe(container)
    return () => {
      if (timeoutId) clearTimeout(timeoutId)
      resizeObserver.disconnect()
    }
  }, [isLoaded])

  // Update map style when styleUrl changes
  useEffect(() => {
    if (!map.current || !styleUrl) return

    // Only update if style actually changed
    if (currentStyleUrlRef.current !== styleUrl) {
      map.current.setStyle(styleUrl)
      currentStyleUrlRef.current = styleUrl
      // Reset style loaded state when style changes
      setStyleLoaded(false)
      
      // Re-check style ready after style change
      map.current.once('styledata', () => {
        if (map.current) {
          map.current.once('idle', () => {
            if (map.current?.isStyleLoaded() && map.current.loaded()) {
              setStyleLoaded(true)
            }
          })
        }
      })
    }
  }, [styleUrl])

  return {
    map,
    mapContainer,
    isLoaded,
    styleLoaded
  }
}

